import type { AppConfig } from '@/fetching/api/appConfig.schema';
import { datadogRum } from '@datadog/browser-rum';
import { useEffect } from 'react';

// Values collected so far in this session. They are kept here so they can be re-sent
// after `setUser` replaces the whole user object.
const featureFlags: Record<string, boolean> = {};
let featureFlagOverrides: Record<string, boolean> = {};
let featureFlagOverridesSignature: string | undefined;
let appConfig: AppConfig | undefined;
let appConfigSignature: string | undefined;

const setUserProperty = (key: string, value: unknown) => {
    datadogRum.setUserProperty(key, value);
};

const trackFeatureFlagValues = (enabledByKey: Record<string, boolean>) => {
    const changed = Object.entries(enabledByKey).filter(
        ([key, enabled]) => featureFlags[key] !== enabled,
    );
    if (changed.length === 0) {
        return;
    }
    changed.forEach(([key, enabled]) => {
        featureFlags[key] = enabled;
    });
    setUserProperty('feature_flags', { ...featureFlags });
};

// Reports resolved feature flag values as `usr.feature_flags` on every event for the rest
// of the session. Callers build a fresh record on each render, so the effect is keyed on a
// signature of the values instead of the record itself.
export const useTrackFeatureFlagValues = (
    enabledByKey: Record<string, boolean>,
    resolved: boolean,
) => {
    const signature = resolved ? JSON.stringify(enabledByKey) : undefined;

    useEffect(() => {
        if (signature) {
            trackFeatureFlagValues(JSON.parse(signature) as Record<string, boolean>);
        }
    }, [signature]);
};

/* Reports the flags a session is forcing as `usr.feature_flag_overrides`, so a session running
 * on overridden values is identifiable rather than looking like every other session.
 */
export const useTrackFeatureFlagOverrides = (overrides: Record<string, boolean>) => {
    const signature = JSON.stringify(overrides);

    useEffect(() => {
        if (signature === featureFlagOverridesSignature) {
            return;
        }
        featureFlagOverridesSignature = signature;
        featureFlagOverrides = JSON.parse(signature) as Record<string, boolean>;
        setUserProperty('feature_flag_overrides', { ...featureFlagOverrides });
    }, [signature]);
};

// Reports the organization's app config as `usr.app_config`. Every caller of `useAppConfig`
// memoizes its own copy of the parsed response, so the content is compared rather than the
// object identity.
export const useTrackAppConfigValues = (config: AppConfig) => {
    useEffect(() => {
        const signature = JSON.stringify(config);
        if (signature === appConfigSignature) {
            return;
        }
        appConfigSignature = signature;
        appConfig = config;
        setUserProperty('app_config', config);
    }, [config]);
};

// Re-sends the collected values, which `setUser` drops when it replaces the user object.
export const applyDatadogSessionAttributes = () => {
    if (Object.keys(featureFlags).length > 0) {
        setUserProperty('feature_flags', { ...featureFlags });
    }
    if (Object.keys(featureFlagOverrides).length > 0) {
        setUserProperty('feature_flag_overrides', { ...featureFlagOverrides });
    }
    if (appConfig) {
        setUserProperty('app_config', appConfig);
    }
};
